import {
  createCard,
  createCollection,
  deleteCard,
  deleteCollection,
  dueSummary,
  enroll,
  forkCollection,
  getTree,
  listCards,
  listEnrolledIds,
  setVisibility,
  shareLink,
  unenroll,
} from "../lib/store";
import { supa } from "../lib/supa";
import { formatShortDate, guessTimeZone } from "../lib/time";
import { lang, t } from "../lib/i18n";
import { englishKey, persianKey } from "../lib/persian";
import { h, mount } from "../ui/dom";
import { navigate, refresh } from "../ui/router";
import { signedIn } from "../ui/app";
import {
  bidiText,
  cardRow,
  collectionCard,
  countsPlaceholder,
  growingField,
  openCardPreview,
  progressPlaceholder,
  spinner,
  statsPlaceholder,
} from "./parts";
import type { Card, Collection } from "../lib/types";

export async function renderCollection(id: string, host: HTMLElement): Promise<void> {
  mount(host, h("div", { class: "screen" }, spinner()));

  const { root, decks } = await getTree(id);
  const collectionIds = [root.id, ...decks.map((d) => d.id)];
  const cards = await listCards(collectionIds);

  const { data } = await supa.auth.getSession();
  // owner_id is NULL on official collections, so isOwner is false for them and
  // none of the editing affordances render — read-only falls out of the model
  // rather than needing a special case.
  const isOwner = !!data.session && root.owner_id === data.session.user.id;
  const enrolled = signedIn ? (await listEnrolledIds()).has(root.root_id) : false;

  const metaText = [
    decks.length ? t("deckCount", { n: decks.length }) : null,
    t("cardCount", { n: cards.length }),
    root.is_official ? t("official") : null,
  ].filter(Boolean).join(" · ");
  // Due/new join this same line once known, exactly like a card's own
  // `.card-stats` — one line for every number about the collection.
  const statsSlot = h("span", { class: "stats-dynamic" });

  const header = h("div", { class: "stack" },
    bidiText(root.title, "screen-title"),
    root.title_fa ? bidiText(root.title_fa, "muted") : null,
    root.description ? h("p", { class: "muted", text: root.description }) : null,
    h("div", { class: "row-meta muted small" },
      metaText ? h("span", { text: metaText }) : null,
      statsSlot,
    ),
  );

  const actions = h("div", { class: "actions" });
  const progressBar = h("div", { class: "card-progress" });
  const studyBar = h("div", { class: "counts study-bar" });

  mount(
    host,
    h("div", { class: "screen stack" },
      header,
      progressBar,
      studyBar,
      actions,
      decksSection(root, decks, cards, isOwner),
    ),
  );

  buildActions(actions, root, isOwner, enrolled, cards.length);
  void attachStudyBar(progressBar, statsSlot, studyBar, root);
}

async function attachStudyBar(
  progressSlot: HTMLElement,
  statsSlot: HTMLElement,
  slot: HTMLElement,
  root: Collection,
): Promise<void> {
  if (!signedIn) return;
  // The bar sits above the actions and the deck list, so arriving late pushed
  // the entire screen down. `full` because this button is full height, unlike
  // the small one on a home row.
  mount(progressSlot, progressPlaceholder());
  mount(statsSlot, ...statsPlaceholder());
  mount(slot, ...countsPlaceholder({ full: true }));
  try {
    const summary = await dueSummary(root.id, guessTimeZone());

    const pills: HTMLElement[] = [];
    if (summary.due) pills.push(h("span", { class: "pill due", text: t("dueToday", { n: summary.due }) }));
    if (summary.fresh) pills.push(h("span", { class: "pill fresh", text: t("newToday", { n: summary.fresh }) }));
    mount(statsSlot, ...pills);

    if (summary.total) {
      const pct = Math.round(((summary.total - summary.notStarted) / summary.total) * 100);
      mount(
        progressSlot,
        h("div", { class: "card-progress-head" },
          h("span", { class: "muted small", text: t("progress") }),
          h("span", { class: "small", text: t("percentValue", { n: pct }) }),
        ),
        h("div", { class: "bar" }, h("div", { class: "bar-fill", attrs: { style: `inline-size: ${pct}%` } })),
      );
    }

    const started = h("span", {
      class: "counts-start muted small",
      text: t("startedOn", { when: formatShortDate(new Date(summary.addedAt), lang()) }),
    });

    mount(
      slot,
      started,
      pills.length
        ? h("button", {
            class: "contrast",
            text: t("studyNow"),
            on: { click: () => navigate(`/study/${root.id}`) },
          })
        : h("span", { class: "muted small", text: t("sessionDone") }),
    );
  } catch (error) {
    mount(slot, h("span", { class: "muted small", text: error instanceof Error ? error.message : "" }));
  }
}

/**
 * Two different things, kept visibly apart.
 *
 * **Enrolling** is about your study list and nothing else — it adds and removes
 * the collection from Home, which is built entirely from enrollments, and never
 * touches content. **Deleting** destroys the content for everyone.
 *
 * They used to share the label "Remove", which was the whole problem: an owner
 * could not tell which one they were about to do, and the owner branch offered
 * only the un-enroll half. Since Browse renders owned collections with no action
 * button, that made it a one-way door — the collection vanished from Home with
 * nothing anywhere to put it back.
 */
function buildActions(
  host: HTMLElement,
  root: Collection,
  isOwner: boolean,
  enrolled: boolean,
  cardCount: number,
): void {
  const children: HTMLElement[] = [];

  // Owners included, deliberately. Your own collection is still something you
  // choose to study or not, and the pair has to be symmetrical or leaving is
  // irreversible.
  //
  // Root pages only: enrollment is per-root, so "Stop studying" on a deck page
  // would leave the whole collection — a promise the label does not make. Study
  // still works from a deck page; it reads the root's enrollment.
  if (signedIn && !root.parent_id) {
    children.push(
      enrolled
        ? h("button", {
            class: "ghost",
            text: t("stopStudying"),
            on: { click: async () => { await unenroll(root.root_id); refresh(); } },
          })
        : h("button", {
            class: "primary",
            text: t("add"),
            // Enroll on the ROOT: that row is both the study grant and, for
            // link-shared collections, the read grant for the whole tree. The
            // un-enroll above must use the same key or it deletes nothing.
            on: { click: async () => { await enroll(root.root_id); refresh(); } },
          }),
    );
  }

  if (signedIn && !isOwner && !root.parent_id) {
    // Editing content other people study must never mutate it — fork first.
    children.push(
      h("button", { class: "ghost", text: t("fork"), on: {
        click: async () => navigate(`/c/${await forkCollection(root.root_id)}`),
      } }),
    );
  }

  if (isOwner) {
    children.push(
      h("button", {
        class: "ghost",
        text: t("edit"),
        on: { click: () => navigate(`/c/${root.id}/edit`) },
      }),
      h("button", {
        class: "ghost",
        text: t("share"),
        on: {
          click: async (event) => {
            const button = event.currentTarget as HTMLButtonElement;
            // Sharing must also lift visibility off 'private', or the recipient's
            // claim succeeds and they still can't read anything.
            if (root.visibility === "private") await setVisibility(root.id, "link");
            const url = shareLink(root);
            try {
              await navigator.clipboard.writeText(url);
              button.textContent = t("shareCopied");
            } catch {
              // Clipboard needs a secure context and can be denied; show the URL
              // so the user can copy it by hand rather than losing the action.
              button.replaceWith(h("input", { type: "text", value: url }));
            }
          },
        },
      }),
      // Named for its scope: this screen renders roots and decks alike, and
      // "Delete" alone would not say which of the two is about to go.
      h("button", {
        class: "danger",
        text: root.parent_id ? t("deleteDeck") : t("deleteCollection"),
        on: { click: () => askToDelete(host, root, cardCount) },
      }),
    );
  }

  mount(host, ...children);
}

/**
 * Replaces the whole action row rather than adding a second step beside it, so
 * there is no way to hit Study or Share while a delete is pending.
 */
function askToDelete(host: HTMLElement, root: Collection, cardCount: number): void {
  const confirm = h("button", {
    class: "danger",
    text: t("deletePermanently"),
    on: {
      click: async () => {
        confirm.disabled = true;
        await deleteCollection(root.id);
        // A deleted deck leaves its parent standing; a deleted root leaves
        // nothing to return to, so fall back to Browse.
        navigate(root.parent_id ? `/c/${root.parent_id}` : "/browse");
      },
    },
  });

  mount(
    host,
    h("div", { class: "stack boxed" },
      h("div", { class: "msg bad", text: t("deleteWarning", { n: cardCount }) }),
      h("div", { class: "actions" },
        confirm,
        h("button", { class: "ghost", text: t("cancel"), on: { click: () => refresh() } }),
      ),
    ),
  );
}

/**
 * Same shape as `askToDelete`, scoped to one card in the list rather than the
 * whole action row — replaces just that row so grading elsewhere on the page
 * (there's none here, but the pattern matches) is never blocked by a pending
 * delete on an unrelated item.
 *
 * `refresh()` for Cancel, same as `askToDelete` — simpler than reconstructing
 * the row's own handlers by hand, and cheap since it's just a re-render of
 * already-fetched data.
 */
function askToDeleteCard(row: HTMLElement, card: Card): void {
  const confirm = h("button", {
    class: "danger",
    text: t("deletePermanently"),
    on: {
      click: async () => {
        confirm.disabled = true;
        await deleteCard(card.id);
        refresh();
      },
    },
  });

  row.replaceWith(
    h("div", { class: "stack boxed" },
      h("div", { class: "msg bad", text: t("deleteCardWarning") }),
      h("div", { class: "actions" },
        confirm,
        h("button", { class: "ghost", text: t("cancel"), on: { click: () => refresh() } }),
      ),
    ),
  );
}

function decksSection(
  root: Collection,
  decks: Collection[],
  cards: Card[],
  isOwner: boolean,
): HTMLElement {
  const section = h("section", { class: "stack" });
  const children: HTMLElement[] = [];

  if (decks.length) {
    children.push(h("h3", { text: t("decks") }));
    for (const deck of decks) {
      children.push(collectionCard(deck, { onOpen: () => navigate(`/c/${deck.id}`), owned: isOwner }));
    }
  }

  // Cards attached directly to this node (a leaf deck, or a flat collection).
  const own = cards.filter((c) => c.collection_id === root.id);
  if (own.length) {
    children.push(h("h3", { text: t("cardCount", { n: own.length }) }));
    for (const card of own) {
      const row = cardRow(card, {
        onOpen: () => openCardPreview(card),
        onDelete: isOwner ? () => askToDeleteCard(row, card) : undefined,
      });
      children.push(row);
    }
  }

  if (!decks.length && !own.length) {
    children.push(h("p", { class: "muted", text: t("emptyCollection") }));
  }

  if (isOwner) {
    children.push(newDeckForm(root), newCardForm(root, cards));
  }

  mount(section, ...children);
  return section;
}

function newDeckForm(root: Collection): HTMLElement {
  const title = h("input", { type: "text", placeholder: t("collectionTitle") });
  const add = h("button", {
    class: "ghost",
    text: t("newDeck"),
    on: {
      click: async () => {
        const name = title.value.trim();
        if (!name) return title.focus();
        add.disabled = true;
        await createCollection({ title: name, parentId: root.id });
        refresh();
      },
    },
  });
  return h("div", { class: "inline-form" }, title, add);
}

function newCardForm(root: Collection, existing: Card[]): HTMLElement {
  const front = growingField(t("front"), { dir: "ltr" });
  const back = growingField(t("backSide"), { dir: "auto" });
  const warning = h("div", { class: "msg", hidden: true });

  const add = h("button", {
    class: "primary",
    text: t("addCard"),
    on: {
      click: async () => {
        if (!front.value.trim() || !back.value.trim()) return front.focus();

        // Duplicate detection uses the folded comparison keys, so an Arabic-vs-
        // Persian keyboard or a stray ZWNJ can't hide a genuine duplicate.
        const fKey = englishKey(front.value);
        const bKey = persianKey(back.value);
        const clash = existing.find(
          (c) => englishKey(c.front) === fKey || persianKey(c.back) === bKey,
        );
        if (clash && warning.hidden) {
          warning.hidden = false;
          warning.className = "msg bad";
          warning.textContent = `${t("duplicateWarning")} (${clash.front} → ${clash.back})`;
          return; // a second click confirms
        }

        add.disabled = true;
        await createCard(root.id, { front: front.value, back: back.value });
        refresh();
      },
    },
  });

  return h("div", { class: "stack boxed" },
    h("h3", { text: t("addCard") }),
    front,
    back,
    warning,
    add,
  );
}
